
import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function TymoPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Innovative Balance Training Technology
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Tymo is a cutting-edge balance training platform featuring advanced pressure sensor technology and interactive biofeedback. The butterfly-shaped design with dual pressure plates provides comprehensive balance assessment and engaging training exercises for patients of all ages and abilities.
              </p>
              <p className="text-gray-700 leading-relaxed">
                With real-time visual feedback and motivating games, Tymo makes balance rehabilitation effective and enjoyable, helping patients improve stability, reduce fall risk, and regain confidence in their movements.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/9c884490f13cae0a728bbb0f35e81249.jpg"
                alt="Tymo Balance Training Platform"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pressure Sensor Technology</h3>
              <p className="text-gray-700 leading-relaxed">
                Advanced pressure sensors detect weight distribution and balance shifts in real-time with high precision, providing accurate data for assessment and training.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Balance Assessment</h3>
                <p className="text-gray-700 leading-relaxed">
                  Comprehensive evaluation of static and dynamic balance capabilities with detailed performance metrics.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Interactive Training Games</h3>
                <p className="text-gray-700 leading-relaxed">
                  Engaging biofeedback games that make balance training motivating and effective for all ages.
                </p>
              </div>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Progress Tracking</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Detailed reports and visual analytics track improvement over time and guide treatment adjustments.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Customizable challenge levels adapt to patient abilities and progression throughout rehabilitation, with specific focus on fall prevention and postural stability improvement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Modes */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Training Modes</h2>
          
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed text-lg">
              Tymo offers diverse training approaches to address different aspects of balance and stability:
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Static Balance Training</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Exercises focused on maintaining stable posture while standing still, improving core stability and postural control.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Dynamic Balance Training</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Activities involving weight shifting and controlled movements to enhance balance during motion and transitions.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Coordination Exercises</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Tasks that challenge coordination between upper and lower body movements while maintaining balance.
                </p>
              </div>
            </div>

            <div className="pt-4">
              <h3 className="font-bold text-gray-900 mb-2">Reaction Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Quick response exercises that improve reflexes and ability to recover from unexpected balance disturbances, essential for fall prevention in real-world situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Clinical Applications</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Tymo is effective across a wide range of neurological and orthopedic conditions requiring balance rehabilitation:
          </p>
          
          <div className="columns-2 md:columns-4 gap-6">
            <p className="text-gray-700 mb-3">Stroke Recovery</p>
            <p className="text-gray-700 mb-3">Parkinson's Disease</p>
            <p className="text-gray-700 mb-3">Multiple Sclerosis</p>
            <p className="text-gray-700 mb-3">Vestibular Disorders</p>
            <p className="text-gray-700 mb-3">Fall Prevention Programs</p>
            <p className="text-gray-700 mb-3">Sports Injury Rehabilitation</p>
            <p className="text-gray-700 mb-3">Elderly Balance Training</p>
            <p className="text-gray-700 mb-3">Post-Surgical Recovery</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Tymo?</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            Tymo combines precision sensor technology with engaging interactive exercises to deliver effective balance rehabilitation. The platform provides objective measurements, motivating feedback, and progressive training programs that help patients achieve better balance, reduce fall risk, and improve quality of life.
          </p>
          <button
            onClick={() => navigate('/equipment')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <span>Explore More Equipment</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
